import { motion } from 'framer-motion';
import { Trophy, Target, Gift, Users, Info } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface GamificationFormProps {
  contentData: Record<string, string>;
  onUpdate: (data: Record<string, string>) => void;
}

export function GamificationForm({ contentData, onUpdate }: GamificationFormProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <div className="flex items-center gap-2">
        <Trophy className="h-5 w-5 text-primary" />
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          QR Gamification
        </h3>
      </div>

      <Card className="p-4 space-y-4">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Game Type</Label>
            <Select 
              value={contentData.gameType || 'scavenger'} 
              onValueChange={v => onUpdate({ ...contentData, gameType: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scavenger">Scavenger Hunt</SelectItem>
                <SelectItem value="scratch">Scratch & Win</SelectItem>
                <SelectItem value="spin">Spin the Wheel</SelectItem>
                <SelectItem value="quiz">Interactive Quiz</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Campaign Name</Label>
            <Input 
              placeholder="Summer Hunt 2026" 
              value={contentData.campaignName || ''} 
              onChange={e => onUpdate({ ...contentData, campaignName: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reward / Prize</Label>
              <Input 
                placeholder="10% Discount" 
                value={contentData.reward || ''} 
                onChange={e => onUpdate({ ...contentData, reward: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Winning Probability (%)</Label>
              <Input 
                type="number"
                placeholder="10" 
                value={contentData.probability || ''} 
                onChange={e => onUpdate({ ...contentData, probability: e.target.value })}
              />
            </div>
          </div>

          <div className="pt-4 border-t">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Game Preview</h4>
            <div className="aspect-video bg-primary/5 rounded-xl border border-primary/20 flex flex-col items-center justify-center p-6 text-center">
              <Gift className="h-10 w-10 text-primary mb-2 animate-bounce" />
              <p className="text-sm font-bold text-primary">Win a Reward!</p>
              <p className="text-[10px] text-muted-foreground mt-1">Users will see an interactive game after scanning.</p>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
